class DlPageScanX extends HTMLElement {
    constructor() {
        super();

        this.video = null;
        this.controlsWrapper = null;
        this.playButton = null;
        this.isInView = false;
        this.userPaused = false;
        this.handlePlayClick = this.handlePlayClick.bind(this);
        this.syncPlayState = this.syncPlayState.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
        this.controlsWrapper = this.querySelector('.dl-page-scan-X-content-left-controls');
        this.playButton = this.querySelector('.dl-page-scan-X-control-play');
        this.video = this.getActiveVideo();

        if (!this.video || !this.playButton) {
            this.hideControls();
            return;
        }

        this.video.muted = true;
        this.playButton.addEventListener('click', this.handlePlayClick);

        this.resizeHandler = () => {
            const activeVideo = this.getActiveVideo();
            if (activeVideo && activeVideo !== this.video) {
                this.video.pause();
                this.detachVideoListeners();
                this.video = activeVideo;
                this.video.muted = true;
                this.attachVideoListeners();
                this.syncPlayState();
                if (this.isInView && !this.userPaused) this.playVideo();
            }
        };

        window.addEventListener('resize', this.resizeHandler);

        this.attachVideoListeners();
        this.syncPlayState();

        this.observer = new IntersectionObserver(this.handleIntersection, {
            threshold: 0.2
        });
        this.observer.observe(this);
    }

    disconnectedCallback() {
        if (this.playButton) {
            this.playButton.removeEventListener('click', this.handlePlayClick);
        }

        this.detachVideoListeners();
        this.observer?.disconnect();

        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    getActiveVideo() {
        const desktopVideo = this.querySelector('.dl-page-scan-X-content-left video.dl-hidden-mobile');
        const mobileVideo = this.querySelector('.dl-page-scan-X-content-left video.dl-hidden-desktop');

        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const candidate = isDesktop ? desktopVideo : mobileVideo;

        return candidate || desktopVideo || mobileVideo;
    }

    attachVideoListeners() {
        if (!this.video) return;

        this.video.addEventListener('play', this.syncPlayState);
        this.video.addEventListener('pause', this.syncPlayState);
        this.video.addEventListener('ended', this.syncPlayState);
        this.video.addEventListener('click', this.handlePlayClick);
    }

    detachVideoListeners() {
        if (!this.video) return;

        this.video.removeEventListener('play', this.syncPlayState);
        this.video.removeEventListener('pause', this.syncPlayState);
        this.video.removeEventListener('ended', this.syncPlayState);
        this.video.removeEventListener('click', this.handlePlayClick);
    }

    hideControls() {
        if (this.controlsWrapper) {
            this.controlsWrapper.classList.add('dl-page-scan-X-controls-hidden');
        }
    }

    handleIntersection([entry]) {
        this.isInView = entry.isIntersecting && entry.intersectionRatio >= 0.2;

        if (this.isInView) {
            if (!this.userPaused) this.playVideo();
            return;
        }

        this.video?.pause();
        this.userPaused = false;
    }

    playVideo() {
        if (!this.video) return;

        this.video.muted = true;
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => this.syncPlayState());
        }
    }

    handlePlayClick() {
        if (!this.video) return;

        if (this.video.paused || this.video.ended) {
            this.userPaused = false;
            this.playVideo();
        } else {
            this.userPaused = true;
            this.video.pause();
        }
    }

    syncPlayState() {
        if (!this.playButton || !this.video) return;

        const isPaused = this.video.paused || this.video.ended;
        this.playButton.classList.toggle('is-paused', isPaused);
        this.playButton.setAttribute('aria-pressed', isPaused ? 'false' : 'true');
        this.playButton.setAttribute('aria-label', isPaused ? '播放视频' : '暂停视频');
    }
}

if (!customElements.get('dl-page-scan-x')) {
    customElements.define('dl-page-scan-x', DlPageScanX);
}

